import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, RefreshCw, KeyRound, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MasterGate } from "@/components/admin/master/MasterAuth";
import { CodeRevealDialog } from "@/components/admin/master/CodeReveal";
import { AdminOverview } from "@/components/admin/AdminOverview";
import { TenantsTab } from "@/components/admin/TenantsTab";
import { PlansTab } from "@/components/admin/PlansTab";
import { CouponsTab } from "@/components/admin/CouponsTab";
import { AnnouncementsTab } from "@/components/admin/AnnouncementsTab";
import { SystemTab } from "@/components/admin/SystemTab";
import { ReceivingTab } from "@/components/admin/ReceivingTab";
import { SupportTab } from "@/components/admin/SupportTab";
import { TermsTab } from "@/components/admin/TermsTab";
import { BoyExpressTab } from "@/components/admin/BoyExpressTab";
import { ReferralsTab } from "@/components/admin/referrals/ReferralsTab";
import { LogTable } from "@/components/LogTable";
import { adminApi, setAdminToken, errMsg } from "@/lib/api";
import { toast } from "sonner";

const fetchAdminLogs = (p) => adminApi.get("/logs", { params: p });

function Dashboard({ onLogout }) {
  const [tick, setTick] = useState(0);
  const [code, setCode] = useState("");
  useEffect(() => { adminApi.get("/me").catch((e) => e.response?.status === 401 && onLogout()); }, [onLogout]);
  const bump = () => setTick((x) => x + 1);
  const rotate = async () => {
    if (!window.confirm("Gerar um novo código de acesso? O código atual deixa de funcionar e outras sessões do Admin Master serão encerradas.")) return;
    try { const { data } = await adminApi.post("/rotate-code"); setAdminToken(data.token); setCode(data.code); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div><p className="eyebrow">FSCsistemas</p><h1 className="text-3xl font-bold">Admin Master</h1></div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" asChild><Link to="/fiscal/admin/integracao" data-testid="admin-fiscal-link"><FileText className="h-4 w-4 mr-1" />Integração fiscal</Link></Button>
          <Button variant="outline" onClick={rotate} data-testid="admin-rotate-code-btn"><KeyRound className="h-4 w-4 mr-1" />Trocar código</Button>
          <Button variant="outline" onClick={bump} data-testid="admin-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={onLogout} data-testid="admin-logout-btn"><LogOut className="h-4 w-4 mr-1" />Sair</Button>
        </div>
      </div>
      <AdminOverview tick={tick} />
      <Tabs defaultValue="clientes">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="clientes" data-testid="admin-tab-clientes">Clientes</TabsTrigger>
          <TabsTrigger value="planos" data-testid="admin-tab-planos">Planos</TabsTrigger>
          <TabsTrigger value="indicacoes" data-testid="admin-tab-indicacoes">Indicações</TabsTrigger>
          <TabsTrigger value="cupons" data-testid="admin-tab-cupons">Cupons</TabsTrigger>
          <TabsTrigger value="recebimento" data-testid="admin-tab-recebimento">Formas de Recebimento</TabsTrigger>
          <TabsTrigger value="suporte" data-testid="admin-tab-suporte">Suporte</TabsTrigger>
          <TabsTrigger value="avisos" data-testid="admin-tab-avisos">Avisos</TabsTrigger>
          <TabsTrigger value="logs" data-testid="admin-tab-logs">Logs</TabsTrigger>
          <TabsTrigger value="parametros" data-testid="admin-tab-parametros">Parâmetros</TabsTrigger>
          <TabsTrigger value="termos" data-testid="admin-tab-termos">Termos de uso</TabsTrigger>
          <TabsTrigger value="boy" data-testid="admin-tab-boy">Boy Express</TabsTrigger>
        </TabsList>
        <TabsContent value="clientes" className="mt-4"><TenantsTab tick={tick} onChanged={bump} /></TabsContent>
        <TabsContent value="planos" className="mt-4"><PlansTab /></TabsContent>
        <TabsContent value="indicacoes" className="mt-4"><ReferralsTab /></TabsContent>
        <TabsContent value="cupons" className="mt-4"><CouponsTab /></TabsContent>
        <TabsContent value="recebimento" className="mt-4"><ReceivingTab /></TabsContent>
        <TabsContent value="suporte" className="mt-4"><SupportTab /></TabsContent>
        <TabsContent value="avisos" className="mt-4"><AnnouncementsTab /></TabsContent>
        <TabsContent value="logs" className="mt-4"><LogTable fetcher={fetchAdminLogs} showTenant testId="admin-logs-table" /></TabsContent>
        <TabsContent value="parametros" className="mt-4"><SystemTab /></TabsContent>
        <TabsContent value="termos" className="mt-4"><TermsTab /></TabsContent>
        <TabsContent value="boy" className="mt-4"><BoyExpressTab /></TabsContent>
      </Tabs>
      <CodeRevealDialog code={code} onClose={() => setCode("")} />
    </div>
  );
}

export default function AdminPanel() {
  const [logged, setLogged] = useState(null);
  useEffect(() => {
    adminApi.get("/me").then(() => setLogged(true)).catch(() => setLogged(false));
  }, []);
  const logout = useCallback(() => { setAdminToken(null); setLogged(false); }, []);
  if (logged === null) return null;
  return logged ? <Dashboard onLogout={logout} /> : <MasterGate onLogged={() => setLogged(true)} />;
}
